import type {
  AssignConfigProfileToClientMutation, AssignConfigProfileToClientMutationVariables,
  ConfigProfileElements,
  ConfigProfiles,
  CreateConfigProfileDto,
  CreateConfigProfileMutation,
  CreateConfigProfileMutationVariables,
  EditConfigProfileElementsMutation, EditConfigProfileElementsMutationVariables, GetConfigProfileElementsQuery,
  GetConfigProfileElementsQueryVariables,
  GetConfigProfileQuery, GetConfigProfileQueryVariables, GetConfigProfilesQuery,
  GetConfigProfilesQueryVariables,
  GetClientConfigProfilesQuery,
  GetClientConfigProfilesQueryVariables, GetClientGroupConfigProfilesQuery, GetClientGroupConfigProfilesQueryVariables,
  GetUserConfigProfilesQuery, GetUserConfigProfilesQueryVariables, GetUserGroupConfigProfilesQuery,
  GetUserGroupConfigProfilesQueryVariables, AssignConfigProfilesToClientGroupsMutation,
  AssignConfigProfilesToClientGroupsMutationVariables, AssignConfigProfilesToUsersMutation,
  AssignConfigProfilesToUsersMutationVariables, AssignConfigProfilesToUserGroupsMutation,
  AssignConfigProfilesToUserGroupsMutationVariables, RemoveClientAssignedConfigProfilesMutation,
  RemoveClientAssignedConfigProfilesMutationVariables, RemoveGroupAssignedConfigProfilesMutation,
  RemoveGroupAssignedConfigProfilesMutationVariables, RemoveUserAssignedConfigProfilesMutation,
  RemoveUserAssignedConfigProfilesMutationVariables, RemoveUserGroupAssignedConfigProfilesMutation,
  RemoveUserGroupAssignedConfigProfilesMutationVariables, ReorderClientAssignedProfilesMutation,
  ReorderClientAssignedProfilesMutationVariables, ReorderGroupAssignedProfilesMutation,
  ReorderUserAssignedProfilesMutation, ReorderUserGroupAssignedProfilesMutation,
  ReorderGroupAssignedProfilesMutationVariables, ReorderUserAssignedProfilesMutationVariables,
  ReorderUserGroupAssignedProfilesMutationVariables,
  DeleteConfigProfileMutationVariables, DeleteConfigProfileMutation, GetConfigProfileStructureQuery,
  GetConfigProfileStructureQueryVariables, LocalizedConfigProfileDto,
  LocalizedConfigProfileStructureDto,
  GetProfilesAssignmentsBatchQuery, GetProfilesAssignmentsBatchQueryVariables,
} from '~/types/graphql';
import createConfigProfile from '@/graphql/mutations/createConfigProfile.graphql';
import editConfigProfile from '@/graphql/mutations/editConfigProfile.graphql';
import getConfigProfiles from '@/graphql/queries/getConfigProfiles.graphql';
import getConfigProfile from '@/graphql/queries/getConfigProfile.graphql';
import getConfigProfileElements from '@/graphql/queries/getConfigProfileElements.graphql';
import getClientConfigProfiles from '@/graphql/queries/getClientConfigProfiles.graphql';
import getClientGroupConfigProfiles from '@/graphql/queries/getClientGroupConfigProfiles.graphql';
import getUserConfigProfiles from '@/graphql/queries/getUserConfigProfiles.graphql';
import getUserGroupConfigProfiles from '@/graphql/queries/getUserGroupConfigProfiles.graphql';
import assignConfigProfileToClient from '@/graphql/mutations/assignConfigProfileToClient.graphql';
import assignConfigProfilesToClientGroups from '@/graphql/mutations/assignConfigProfileToClientGroup.graphql';
import assignConfigProfilesToUsers from '@/graphql/mutations/assignConfigProfilesToUsers.graphql';
import assignConfigProfilesToUserGroups from '@/graphql/mutations/assignConfigProfilesToUserGroups.graphql';
import removeClientAssignedConfigProfiles from '@/graphql/mutations/removeClientAssignedConfigProfiles.graphql';
import removeGroupAssignedConfigProfiles from '@/graphql/mutations/removeGroupAssignedConfigProfiles.graphql';
import removeUserAssignedConfigProfiles from '@/graphql/mutations/removeUserAssignedConfigProfiles.graphql';
import removeUserGroupAssignedConfigProfiles from '@/graphql/mutations/removeUserGroupAssignedConfigProfiles.graphql';
import reorderClientAssignedProfiles from '@/graphql/mutations/reorderClientAssignedProfiles.graphql';
import reorderGroupAssignedProfiles from '@/graphql/mutations/reorderGroupAssignedProfiles.graphql';
import reorderUserAssignedProfiles from '@/graphql/mutations/reorderUserAssignedProfiles.graphql';
import reorderUserGroupAssignedProfiles from '@/graphql/mutations/reorderUserGroupAssignedProfiles.graphql';
import deleteConfigProfile from '@/graphql/mutations/deleteConfigProfile.graphql';
import getConfigProfileStructureQuery from '@/graphql/queries/getConfigProfileStructure.graphql';
import getProfilesAssignmentsBatch from '../graphql/queries/getProfilesAssignmentsBatch.graphql';
import getProfilesForAssignment from '@/graphql/queries/getProfilesForAssignment.graphql';
import { defineStore } from 'pinia';
import { buildProfileElementLookupMap } from '~/Common/ConfigHelpers';
import { ref } from 'vue';
import { computed, readonly } from 'vue';


export const useConfigProfilesStore = defineStore("configProfiles", () =>{

  // State
  const loading = ref(false);
  const profiles = ref<ConfigProfiles[]>([]);
  const configProfileId = ref<number | null>(null);
  const configProfileElements = ref<ConfigProfileElements[]>([]);
  const profileElementLookupMap = ref<
    Map<number, { element: LocalizedConfigProfileDto; moduleInfo: LocalizedConfigProfileStructureDto }>
  >(new Map());
  const getAllProfilesInFlight = ref<Promise<ConfigProfiles[]> | null>(null);
  // Computed properties
  const isLoading = computed(() => loading.value);
  const configProfileStructure = ref<GetConfigProfileStructureQuery['getConfigProfileStructure'] | null>(null);

  // get the current profile name
  const currentProfileName = computed(() => {
    const profile = profiles.value.find(p => p.id === configProfileId.value);
    return profile ? profile.profileName : null;
  });
  async function createProfile(profile: CreateConfigProfileDto): Promise<CreateConfigProfileMutation | null> {
    const res = await storeGraphqlMutation<CreateConfigProfileMutation, CreateConfigProfileMutationVariables>(
      {
        mutation: createConfigProfile,
        variables: {
          ...profile
        },
      },
      'Error while creating configuration profile'
    );

    if (res?.data?.createConfigProfile) {
      await getAllProfiles();
    }

    return res?.data || null;
  }

  async function updateProfile(changes: {
    elementId: number,
    elementValue: string; }[]
  ): Promise<EditConfigProfileElementsMutation | null> {
      const res = await storeGraphqlMutation<EditConfigProfileElementsMutation, EditConfigProfileElementsMutationVariables>(
      {
          mutation: editConfigProfile,
        variables: {
          changes,
        },
      },
      'Error while updating configuration profile'
    );
    return res?.data || null;
  }

  async function getAllProfiles() {
    if (getAllProfilesInFlight.value) {
      return getAllProfilesInFlight.value;
    }

    getAllProfilesInFlight.value = (async () => {
      loading.value = true;
      try {
        const res = await storeGraphqlQuery<GetConfigProfilesQuery, GetConfigProfilesQueryVariables>(
          {
            query: getConfigProfiles,
            variables: {},
          },
          'Error while loading configuration profiles'
        );
        const fetchedProfiles = (res?.data?.getConfigProfiles ?? []) as ConfigProfiles[];
        profiles.value = fetchedProfiles;
        return fetchedProfiles;
      } finally {
        loading.value = false;
      }
    })();

    try {
      return await getAllProfilesInFlight.value;
    } finally {
      getAllProfilesInFlight.value = null;
    }
  }

  async function getProfile(id: number): Promise<GetConfigProfileQuery | null> {
    loading.value = true;
    try{
      const res = await storeGraphqlQuery<GetConfigProfileQuery, GetConfigProfileQueryVariables>(
        {
          query: getConfigProfile,
          variables: {
            id
          },
        },
        'Error while loading configuration profile'
      );
      if(res?.data) {
        configProfileId.value = res.data.getConfigProfile.id!;
        return res.data;
      }
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function getProfileElements(id: number): Promise<GetConfigProfileElementsQuery | null> {
    loading.value = true;
    try {
      console.log('Requesting elements for profile id:', id);
      const res = await storeGraphqlQuery<GetConfigProfileElementsQuery, GetConfigProfileElementsQueryVariables>(
        {
          query: getConfigProfileElements,
          variables: { id },
        },
        'Error while loading configuration profile elements'
      );
      console.log('Full GraphQL response:', res);
      if (res?.data) {
        configProfileElements.value = (res.data.getConfigProfileElements || []).map((el: any) => ({
          ...el,
          elementPath: typeof el.elementPath === 'object' && el.elementPath !== null
            ? el.elementPath.xpath
            : el.elementPath,
        }));
        return res.data;
      }
      return null;
    } finally {
      loading.value = false;
    }
  }


  async function getProfileStructure(profileId: number, languageCode: string) {
    loading.value = true;
    try {
      const res = await storeGraphqlQuery<GetConfigProfileStructureQuery, GetConfigProfileStructureQueryVariables>(
        {
          query: getConfigProfileStructureQuery,
          variables: { profileId, languageCode },
        },
        'Error while loading configuration profile structure'
      );

      if (res?.data) {
        const blocks = (res.data.getConfigProfileStructure ?? []) as LocalizedConfigProfileStructureDto[];
        // optional but useful for O(1) lookups like the config store does
        profileElementLookupMap.value = buildProfileElementLookupMap(blocks);
      }

      return res?.data ?? null;
    } finally {
      loading.value = false;
    }
  }

  async function assignProfileToClient(
    configProfileId: number[],
    clientId: number,
    options?: { refreshClient?: boolean }
  ): Promise<AssignConfigProfileToClientMutation | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlMutation<AssignConfigProfileToClientMutation, AssignConfigProfileToClientMutationVariables>(
        {
          mutation: assignConfigProfileToClient,
          variables: {
            configProfileId: configProfileId,
            clientId: clientId,
          },
        },
        'Error while assigning configuration profile to client'
      );
      // Refresh the affected client in the store so hasAssignedProfiles
      // updates in real-time without waiting for a subscription event.
      if (res?.data && options?.refreshClient !== false) {
        const { useClientsStore } = await import('~/stores/clients');
        await useClientsStore().refreshClientInStore(clientId);
      }
      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }

  async function assignProfileToGroup(groupId: number, profileIds: number[]): Promise<AssignConfigProfilesToClientGroupsMutation | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlMutation<AssignConfigProfilesToClientGroupsMutation, AssignConfigProfilesToClientGroupsMutationVariables>(
        {
          mutation: assignConfigProfilesToClientGroups,
          variables: {
            profileId: profileIds,
            groupId,
          }
        },
        'Error while assigning configuration profile to group'
      );
      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }

  async function assignProfileToUser(profileId: number[], userId: number): Promise<AssignConfigProfilesToUsersMutation | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlMutation<AssignConfigProfilesToUsersMutation, AssignConfigProfilesToUsersMutationVariables>(
        {
          mutation: assignConfigProfilesToUsers,
          variables: {
            userId,
            configProfileId: profileId
          }
        },
        'Error while assigning configuration profile to user'
      );
      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }

  async function assignProfileToUserGroup(profileId: number[], userGroupId: number): Promise<AssignConfigProfilesToUserGroupsMutation | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlMutation<AssignConfigProfilesToUserGroupsMutation, AssignConfigProfilesToUserGroupsMutationVariables>(
        {
          mutation: assignConfigProfilesToUserGroups,
          variables: {
            userGroupId,
            configProfileId: profileId
          }
        },
        'Error while assigning configuration profile to user group'
      );
      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }

  async function removeClientAssignedProfile(
    profileId: number[],
    clientId: number,
    options?: { refreshClient?: boolean }
  ): Promise<RemoveClientAssignedConfigProfilesMutation | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlMutation<RemoveClientAssignedConfigProfilesMutation, RemoveClientAssignedConfigProfilesMutationVariables>(
        {
          mutation: removeClientAssignedConfigProfiles,
          variables: {
            clientId,
            configProfileIds: profileId,
          }
        },
        'Error while removing configuration profile from client'
      );
      // Refresh the affected client in the store so hasAssignedProfiles
      // updates in real-time without waiting for a subscription event.
      if (res?.data && options?.refreshClient !== false) {
        const { useClientsStore } = await import('~/stores/clients');
        await useClientsStore().refreshClientInStore(clientId);
      }
      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }

  async function removeGroupAssignedProfile(groupId: number, profileId: number[]): Promise<RemoveGroupAssignedConfigProfilesMutation | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlMutation<RemoveGroupAssignedConfigProfilesMutation, RemoveGroupAssignedConfigProfilesMutationVariables>(
        {
          mutation: removeGroupAssignedConfigProfiles,
          variables: {
            groupId,
            configProfileIds: profileId,
          }
        },
        'Error while removing configuration profile from group'
      );
      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }

  async function removeUserAssignedProfile(userId: number, profileId: number[]): Promise<RemoveUserAssignedConfigProfilesMutation | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlMutation<RemoveUserAssignedConfigProfilesMutation, RemoveUserAssignedConfigProfilesMutationVariables>(
        {
          mutation: removeUserAssignedConfigProfiles,
          variables: {
            userId,
            configProfileIds: profileId,
          }
        },
        'Error while removing configuration profile from user'
      );
      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }

  async function removeUserGroupAssignedProfile(userGroupId: number, profileId: number[]): Promise<RemoveUserGroupAssignedConfigProfilesMutation | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlMutation<RemoveUserGroupAssignedConfigProfilesMutation, RemoveUserGroupAssignedConfigProfilesMutationVariables>(
        {
          mutation: removeUserGroupAssignedConfigProfiles,
          variables: {
            userGroupId,
            configProfileIds: profileId,
          }
        },
        'Error while removing configuration profile from user group'
      );
      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }

  async function reorderClientProfiles(clientId: number, profileIds: number[]): Promise<ReorderClientAssignedProfilesMutation | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlMutation<ReorderClientAssignedProfilesMutation, ReorderClientAssignedProfilesMutationVariables>(
        {
          mutation: reorderClientAssignedProfiles,
          variables: {
            clientId,
            profileIds: [...profileIds],
          }
        },
        'Error while reordering client assigned configuration profiles'
      );
      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }

  async function reorderGroupProfiles(groupId: number, profileIds: number[]): Promise<ReorderGroupAssignedProfilesMutation | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlMutation<ReorderGroupAssignedProfilesMutation, ReorderGroupAssignedProfilesMutationVariables>(
        {
          mutation: reorderGroupAssignedProfiles,
          variables: {
            groupId,
            profileIds: [...profileIds],
          }
        },
        'Error while reordering group assigned configuration profiles'
      );
      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }

  async function reorderUserProfiles(userId: number, profileIds: number[]): Promise<ReorderUserAssignedProfilesMutation | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlMutation<ReorderUserAssignedProfilesMutation, ReorderUserAssignedProfilesMutationVariables>(
        {
          mutation: reorderUserAssignedProfiles,
          variables: {
            userId,
            profileIds: [...profileIds],
          }
        },
        'Error while reordering user assigned configuration profiles'
      );
      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }

  async function reorderUserGroupProfiles(userGroupId: number, profileIds: number[]): Promise<ReorderUserGroupAssignedProfilesMutation | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlMutation<ReorderUserGroupAssignedProfilesMutation, ReorderUserGroupAssignedProfilesMutationVariables>(
        {
          mutation: reorderUserGroupAssignedProfiles,
          variables: {
            userGroupId,
            profileIds: [...profileIds],
          }
        },
        'Error while reordering user group assigned configuration profiles'
      );
      return res?.data || null;
    }
    finally {
      loading.value = false;
    }
  }

  async function getClientAssignedProfiles(clientId: number): Promise<GetClientConfigProfilesQuery | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlQuery<GetClientConfigProfilesQuery, GetClientConfigProfilesQueryVariables>(
        {
          query: getClientConfigProfiles,
          variables: {
            clientId,
          }
        },
        'Error while loading client assigned configuration profiles'
      );
      return res?.data || null;
    }
    finally {
      loading.value = false;
    }
  }

  async function getGroupAssignedProfiles(groupId: number): Promise<GetClientGroupConfigProfilesQuery | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlQuery<GetClientGroupConfigProfilesQuery, GetClientGroupConfigProfilesQueryVariables>({
          query: getClientGroupConfigProfiles,
          fetchPolicy: 'network-only',
          variables: {
            groupId,
          }
        },
        'Error while loading group assigned configuration profiles'
      );
      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }

  async function getUserAssignedProfiles(userId: number): Promise<GetUserConfigProfilesQuery | null> {
    loading.value = true;
    try{
      const res = await storeGraphqlQuery<GetUserConfigProfilesQuery, GetUserConfigProfilesQueryVariables>(
        {
          query: getUserConfigProfiles,
          variables: {
            userId,
          }
        },
        'Error while loading user assigned configuration profiles'
      );
      return res?.data || null;
    } finally {
      loading.value = false;
    }
  }

  async function getUserGroupAssignedProfiles(userGroupId: number): Promise<GetUserGroupConfigProfilesQuery | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlQuery<GetUserGroupConfigProfilesQuery, GetUserGroupConfigProfilesQueryVariables>(
        {
          query: getUserGroupConfigProfiles,
          variables: {
            userGroupId,
          }
        },
        'Error while loading user group assigned configuration profiles'
      );
      return res?.data || null;
    }
    finally {
      loading.value = false;
    }
  }

  async function deleteProfile(profileId: number): Promise<DeleteConfigProfileMutation | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlMutation<DeleteConfigProfileMutation, DeleteConfigProfileMutationVariables>(
        {
          mutation: deleteConfigProfile,
          variables: {
            profileId,
          },
        },
        'Error while deleting configuration profile'
      );
      if (configProfileId.value === profileId) {
        configProfileId.value = null;
      }

      await getAllProfiles();
      return res?.data || null;
    }
    finally {
      loading.value = false;
    }
  }

async function getProfilesAssignments(profileIds: number[]): Promise<GetProfilesAssignmentsBatchQuery | null> {
    loading.value = true;
    try {
      const res = await storeGraphqlQuery<GetProfilesAssignmentsBatchQuery, GetProfilesAssignmentsBatchQueryVariables>(
        {
          query: getProfilesAssignmentsBatch,
          variables: {
            profileIds,
          }
        },
        'Error while loading profile assignments batch'
      );
      return res?.data || null;
    } finally {
      loading.value = false;
    }
}

  async function getProfilesForAssignmentStore(targetType: 'clientGroup' | 'client' | 'user' | 'userGroup', targetKey: string): Promise<{ id: number; profileName: string }[]> {
    loading.value = true;
    try {
      const res = await storeGraphqlQuery<any, { targetType: string; targetKey: string }>(
        {
          query: getProfilesForAssignment,
          variables: { targetType, targetKey },
        },
        'Error while fetching assignable profiles'
      );
      return res?.data?.getProfilesForAssignment || [];
    } finally {
      loading.value = false;
    }
  }

  return {
    createProfile,
    updateProfile,
    getAllProfiles,
    getProfile,
    getProfileElements,
    configProfileStructure,
    assignProfileToClient,
    assignProfileToGroup,
    assignProfileToUser,
    assignProfileToUserGroup,
    removeClientAssignedProfile,
    removeGroupAssignedProfile,
    removeUserAssignedProfile,
    removeUserGroupAssignedProfile,
    reorderClientProfiles,
    reorderGroupProfiles,
    reorderUserProfiles,
    reorderUserGroupProfiles,
    getClientAssignedProfiles,
    getGroupAssignedProfiles,
    getUserAssignedProfiles,
    getUserGroupAssignedProfiles,
    getProfileStructure,
    getProfilesAssignments,
    getProfilesForAssignmentStore,
    currentProfileName,
    configProfileElements,
    deleteProfile,
    configProfileId,
    isLoading,
    loading,
    profiles,
    getProfileElementLookupMap: readonly(profileElementLookupMap),
  }
});

